import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Paper,
  InputBase,
  IconButton,
  Popper,
  List,
  ListItem,
  ListItemText,
  Box,
  styled,
  alpha,
  CircularProgress,
  Typography,
  Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { getCompactLogoPath } from '../../../../resources/UtilityCode/UtilityFunctions';
import debounce from 'lodash/debounce';
import { useScreenSize } from '../../../../context/ScreenSizeContext';
import api from '../../../../api';
import { analyticsV2 } from '../../../../analytics/analyticsV2';
import {
  fetchStockData,
  STOCK_DATA_STALE_TIME,
} from '../../../../hooks/stocksHooks/useStockData';


const SearchWrapper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: 'auto',
  height: 48,
  borderRadius: 24,
  backgroundColor: '#F2F2F7', // iOS search field background
  border: '1px solid rgba(0,0,0,0.06)',
  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
  transition: 'box-shadow 180ms ease, border-color 180ms ease, background-color 180ms ease',
  '&:hover, &:focus-within': {
    backgroundColor: '#FFFFFF',
    borderColor: 'rgba(0,0,0,0.08)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  marginLeft: 16,
  flex: 1,
  '& .MuiInputBase-input': {
    padding: '8px 0',
    fontSize: '1rem',
    transition: 'width 0.3s ease',
    width: '100%',
    '::placeholder': {
      color: '#8E8E93', // iOS placeholder gray
      opacity: 1,
    },
  },
}));

const SearchIconWrapper = styled(IconButton)(({ theme }) => ({
  padding: 10,
  color: '#007AFF', // iOS blue
  '&:hover': {
    backgroundColor: alpha('#007AFF', 0.06),
  },
}));

const SuggestionsPopper = styled(Popper)(({ theme }) => ({
  zIndex: 1300,
  width: 'auto',
  marginTop: '4px',
}));

const SuggestionsList = styled(List)(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.85)',
  backdropFilter: 'saturate(180%) blur(16px)',
  WebkitBackdropFilter: 'saturate(180%) blur(16px)',
  borderRadius: 16,
  border: '1px solid rgba(0,0,0,0.06)',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  padding: '6px 0',
  maxHeight: '420px',
  overflow: 'auto',
}));

const SuggestionItem = styled(ListItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 16px',
  cursor: 'pointer',
  borderBottom: '1px solid rgba(0,0,0,0.06)',
  '&:last-of-type': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  transition: 'background-color 120ms ease',
}));

const TickerImage = styled('img')({
  width: 40,
  height: 40,
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
});

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '16px',
});

const NoResultsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '16px',
  color: '#666',
});

interface Ticker {
  ticker: string;
  General: {
    Name: string;
    LogoURL: string;
  };
  Highlights: {
    MarketCapitalization: number;
  };
}

interface SearchBarProps {
  data: Ticker[];
  onIntent?: () => void;
}


// Main component
const SearchBar: React.FC<SearchBarProps> = ({ data, onIntent }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<Ticker[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [externalResults, setExternalResults] = useState<Array<{ Name: string; Code: string; Exchange: string }>>([]);
  const [isLoadingExternal, setIsLoadingExternal] = useState(false);
  const [externalSource, setExternalSource] = useState<"button" | "auto" | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useScreenSize();
  const lastExternalQueryRef = useRef<string>("");

  // Check if data is available
  const isDataAvailable = Array.isArray(data) && data.length > 0;

  // Debounced search function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      if (term.length > 0) {
        if (isDataAvailable) {
          const filtered = data.filter(
            item =>
              item.ticker.toLowerCase().includes(term.toLowerCase()) ||
              item.General.Name.toLowerCase().includes(term.toLowerCase())
          );
          if (term.trim().length >= 2) {
            analyticsV2.trackDebounced(
              'search_performed',
              {
                query: term.trim().slice(0, 160),
                resultCount: filtered.length,
                source: 'local',
                action: 'results',
              },
              500,
              'header-search'
            );
          }
          setSuggestions(filtered.slice(0, 7));
          setNoResults(filtered.length === 0);
        }
        setIsLoading(false);
      } else {
        setSuggestions([]);
        setNoResults(false);
        setIsLoading(false);
        setExternalResults([]);
      }
    }, 300),
    [data, isDataAvailable]
  );

  // Handle input change
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    // Anchor to the full wrapper so the popper matches the pill width
    if (wrapperRef.current) setAnchorEl(wrapperRef.current);

    if (value.length > 0) {
      setIsLoading(true);
      setNoResults(false);
    }

    debouncedSearch(value);
    setExternalResults([]);
    lastExternalQueryRef.current = "";
  };

  const warmStockPage = useCallback((ticker: string) => {
    void import('../../../../pages/stockPage/stockPage');
    void queryClient.prefetchQuery({
      queryKey: ['stockData', ticker],
      queryFn: ({ signal }) => fetchStockData(ticker, signal),
      staleTime: STOCK_DATA_STALE_TIME,
    });
  }, [queryClient]);

  // Handle suggestion click
  const handleSuggestionClick = (
    ticker: string,
    resultSource: 'local' | 'external',
    name?: string
  ) => {
    analyticsV2.track('search_performed', {
      query: searchTerm.trim().slice(0, 160),
      resultTicker: ticker.toUpperCase(),
      source: resultSource,
      action: 'select',
    });
    warmStockPage(ticker);
    navigate(`/stocks/${ticker}`, {
      state: {
        stockPreview: {
          ticker,
          name: name || ticker,
        },
      },
    });
    setSearchTerm('');
    setSuggestions([]);
    setNoResults(false);
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('.MuiPopper-root')
      ) {
        setSuggestions([]);
        setNoResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile]);

  // Watch for data availability
  const prevDataAvailableRef = useRef<boolean>(isDataAvailable);
  useEffect(() => {
    const wasAvailable = prevDataAvailableRef.current;
    // When data transitions from unavailable -> available while user is typing,
    // keep the popper open by setting loading and triggering a search.
    if (!wasAvailable && isDataAvailable && searchTerm.trim().length > 0) {
      setIsLoading(true);
      debouncedSearch(searchTerm);
    }
    prevDataAvailableRef.current = isDataAvailable;
  }, [isDataAvailable, searchTerm, debouncedSearch]);

  const isBootLoading = !isDataAvailable && searchTerm.trim().length > 0;

  // Compute a popper width slightly wider than the pill on mobile (not full width)
  const anchorWidth = anchorEl && (anchorEl as HTMLElement).clientWidth ? (anchorEl as HTMLElement).clientWidth : 0;
  const mobileExtraWidth = 48; // expand beyond the pill moderately
  const viewportPadding = 24; // keep decent padding from viewport edges
  const viewportWidth = (typeof window !== 'undefined') ? window.innerWidth : undefined;
  const maxMobileWidth = viewportWidth ? Math.max(0, viewportWidth - viewportPadding * 2) : undefined;
  const popupWidth = isMobile && anchorWidth
    ? Math.min(anchorWidth + mobileExtraWidth, (maxMobileWidth ?? anchorWidth + mobileExtraWidth))
    : (anchorWidth || undefined);
  const popupMarginLeft = isMobile && anchorWidth && popupWidth
    ? -((popupWidth - anchorWidth) / 2)
    : 0;

  // Robust no-results fallback (covers both local and external empty states)
  const noResultsEffective = !isBootLoading && !isLoading && !isLoadingExternal && searchTerm.trim().length > 0 && suggestions.length === 0 && externalResults.length === 0;

  // Keep the popper open also when we are in the effective no-results state
  const showPopup = (isBootLoading || isLoading || suggestions.length > 0 || noResultsEffective || externalResults.length > 0 || isLoadingExternal) && searchTerm.length > 0;

  const handleExternalSearch = React.useCallback(async (source: "button" | "auto") => {
    const term = searchTerm.trim();
    if (!term) return;
    // Immediately remember this query so the "See more" button does not reappear
    // even if the upstream request fails or times out.
    lastExternalQueryRef.current = term;
    try {
      setExternalSource(source);
      setIsLoadingExternal(true);
      setNoResults(false);
      const { data } = await api.get('searchStocks', { params: { q: term } });
      const items = Array.isArray(data?.items) ? data.items : [];
      analyticsV2.track('search_performed', {
        query: term.slice(0, 160),
        resultCount: items.length,
        source: 'external',
        trigger: source,
        action: 'results',
      });
      setExternalResults(items);
      setSuggestions([]);
      setNoResults(items.length === 0);
    } catch (e) {
      setExternalResults([]);
    } finally {
      setIsLoadingExternal(false);
      setExternalSource(null);
    }
  }, [searchTerm]);

  // External results: click navigates directly; stock page handles fallback add

  // Auto-trigger external search when local results drop to zero
  useEffect(() => {
    const term = searchTerm.trim();
    if (
      term.length > 0 &&
      !isLoading &&
      !isLoadingExternal &&
      noResults &&
      externalResults.length === 0 &&
      lastExternalQueryRef.current !== term
    ) {
      handleExternalSearch("auto");
    }
  }, [noResults, isLoading, isLoadingExternal, externalResults.length, searchTerm, handleExternalSearch]);

  return (
    <Box sx={{
      width: '100%',
      maxWidth: isMobile ? '100%' : '560px',
      mx: 'auto'
    }} ref={wrapperRef}>
      <SearchWrapper
        elevation={0}
        sx={{
          width: '100%',
          height: isMobile ? '36px' : '42px',
          borderRadius: isMobile ? '18px' : '21px',
        }}
        onClick={() => {
          onIntent?.();
          if (isMobile) {
            inputRef.current?.focus();
          }
        }}
      >
        <StyledInputBase
          placeholder={isMobile ? "search stock" : "Search stocks by name or ticker"}
          value={searchTerm}
          onChange={handleSearch}
          onFocus={() => {
            onIntent?.();
            // Ensure anchor is the wrapper on focus too
            if (wrapperRef.current) setAnchorEl(wrapperRef.current);
          }}
          fullWidth
          inputRef={inputRef}
          type="search"
          inputProps={{
            inputMode: 'search',
            autoCapitalize: 'none',
            autoCorrect: 'off',
            enterKeyHint: 'search',
          } as any}
          sx={{
            marginLeft: isMobile ? '8px' : '12px',
            '& .MuiInputBase-input': {
              padding: isMobile ? '6px 0' : '6px 0',
              // iOS zoom fix: keep >=16px on mobile
              fontSize: isMobile ? '16px' : '0.95rem',
            }
          }}
        />
        <SearchIconWrapper
          aria-label="Focus stock search"
          onClick={() => {
            onIntent?.();
            inputRef.current?.focus();
          }}
          sx={{
            padding: isMobile ? '4px' : '8px',
          }}
        >
          <SearchIcon fontSize={isMobile ? "small" : "small"} />
        </SearchIconWrapper>
      </SearchWrapper>

      <SuggestionsPopper
        open={showPopup}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{
          width: popupWidth,
          maxWidth: '100vw',
          ml: popupMarginLeft,
        }}
      >
        <SuggestionsList>
          {isBootLoading && (
            <>
              {Array.from({ length: 6 }).map((_, idx) => (
                <SuggestionItem key={`skeleton-${idx}`} sx={{ minHeight: isMobile ? 56 : 64 }}>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Skeleton variant="text" width="80%" height={isMobile ? 18 : 22} />
                    <Skeleton variant="text" width="40%" height={isMobile ? 14 : 18} />
                  </Box>
                  <Skeleton variant="circular" width={isMobile ? 30 : 40} height={isMobile ? 30 : 40} />
                </SuggestionItem>
              ))}
            </>
          )}

          {!isBootLoading && isLoading && (
            <LoadingContainer>
              <CircularProgress size={isMobile ? 24 : 32} color="primary" />
            </LoadingContainer>
          )}

          {!isBootLoading && !isLoading && isLoadingExternal && externalSource !== "button" && (
            <LoadingContainer>
              <CircularProgress size={isMobile ? 24 : 32} color="primary" />
            </LoadingContainer>
          )}



          {(noResultsEffective) && (
            <NoResultsContainer>
              <SearchOffIcon sx={{ fontSize: isMobile ? 32 : 48, mb: 1 }} />
              <Typography variant={isMobile ? 'body2' : 'body1'}>
                No results for "{searchTerm}"
              </Typography>
            </NoResultsContainer>
          )}

          {!isBootLoading && !isLoading && suggestions.map(item => (
            <SuggestionItem
              key={item.ticker}
              onPointerEnter={() => warmStockPage(item.ticker)}
              onTouchStart={() => warmStockPage(item.ticker)}
              onFocus={() => warmStockPage(item.ticker)}
              onClick={() =>
                handleSuggestionClick(item.ticker, 'local', item.General.Name)
              }
              sx={{ minHeight: isMobile ? 56 : 64 }}
            >
              <ListItemText
                sx={{ flex: 1 }}
                primary={item.General.Name}
                secondary={item.ticker}
                primaryTypographyProps={{
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  color: 'text.primary',
                  noWrap: true,
                  sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                }}
                secondaryTypographyProps={{
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  color: 'text.secondary',
                  noWrap: true,
                  sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                }}
              />
              <TickerImage
                src={getCompactLogoPath(item.ticker, item.General.LogoURL)}
                alt={`${item.ticker} logo`}
                sx={{
                  width: isMobile ? '30px' : '40px',
                  height: isMobile ? '30px' : '40px',
                }}
              />
            </SuggestionItem>
          ))}

          {/* Bottom area: See more or spinner (when button-triggered external search) */}
          {!isBootLoading && !isLoading && !noResults && suggestions.length > 0 && suggestions.length <= 3 && externalResults.length === 0 && (lastExternalQueryRef.current !== searchTerm.trim()) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
              {isLoadingExternal && externalSource === "button" ? (
                <CircularProgress size={20} />
              ) : (
                <IconButton
                  aria-label="Search more stocks"
                  color="primary"
                  onClick={() => handleExternalSearch("button")}
                  disabled={isLoadingExternal}
                >
                  <Typography variant={isMobile ? 'body2' : 'body2'}>See more</Typography>
                </IconButton>
              )}
            </Box>
          )}

          {/* External results (replace list) */}
          {!isBootLoading && !isLoading && externalResults.length > 0 && (
            <>
              {externalResults.map((ext, idx) => (
                <SuggestionItem
                  key={`${ext.Code}.${ext.Exchange}.${idx}`}
                  onPointerEnter={() =>
                    warmStockPage(`${ext.Code}.${ext.Exchange}`)
                  }
                  onTouchStart={() =>
                    warmStockPage(`${ext.Code}.${ext.Exchange}`)
                  }
                  onFocus={() =>
                    warmStockPage(`${ext.Code}.${ext.Exchange}`)
                  }
                  onClick={() =>
                    handleSuggestionClick(
                      `${ext.Code}.${ext.Exchange}`,
                      'external',
                      ext.Name
                    )
                  }
                  sx={{ minHeight: isMobile ? 48 : 56 }}
                >
                  <ListItemText
                    sx={{ flex: 1 }}
                    primary={ext.Name}
                    secondary={`${ext.Code}.${ext.Exchange}`}
                    primaryTypographyProps={{
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      color: 'text.primary',
                      noWrap: true,
                      sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                    }}
                    secondaryTypographyProps={{
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                      color: 'text.secondary',
                      noWrap: true,
                      sx: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                    }}
                  />
                  <TickerImage
                    src={getCompactLogoPath(`${ext.Code}.${ext.Exchange}`)}
                    alt={`${ext.Code}.${ext.Exchange} logo`}
                    loading="lazy"
                    decoding="async"
                    sx={{ width: isMobile ? '30px' : '40px', height: isMobile ? '30px' : '40px' }}
                  />
                </SuggestionItem>
              ))}
            </>
          )}
        </SuggestionsList>
      </SuggestionsPopper>
    </Box>
  );
};

export default SearchBar;
